import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./StoreMap.css";
import { MapPin, Store } from "lucide-react";

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom Walmart store icon
const createWalmartIcon = () => {
  return L.divIcon({
    html: `
      <div style="
        background-color: #0071ce;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        color: white;
        font-size: 12px;
      ">W</div>
    `,
    className: "custom-walmart-icon",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });
};

interface WalmartStore {
  store_id: string;
  postal_code: string;
  address: string;
  geo: {
    lat: number;
    long: number;
  };
}

interface StoreMapProps {
  stores?: WalmartStore[];
  selectedStoreId?: string;
  center?: [number, number];
  zoom?: number;
  height?: string;
  className?: string;
  tileUrl?: string;
}

export default function StoreMap({
  stores = [],
  selectedStoreId,
  center = [39.8283, -98.5795],
  zoom = 4,
  height = "400px",
  className = "",
  tileUrl,
}: StoreMapProps) {
  const walmartIcon = createWalmartIcon();

  // If we have a selected store, center on it
  const mapCenter =
    selectedStoreId && stores.length > 0
      ? (() => {
          const selectedStore = stores.find(
            (store) => store.store_id === selectedStoreId
          );
          return selectedStore
            ? ([selectedStore.geo.lat, selectedStore.geo.long] as [
                number,
                number
              ])
            : center;
        })()
      : center;

  const mapZoom = selectedStoreId ? 13 : zoom;

  return (
    <div
      className={`relative store-map-container ${className}`}
      style={{ height }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/20 to-primary-100/20 rounded-xl pointer-events-none z-0" />
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: "100%", width: "100%", borderRadius: "8px" }}
        scrollWheelZoom={true}
        className="store-map"
        zoomControl={true}
        attributionControl={true}
        doubleClickZoom={false}
        dragging={true}
        touchZoom={true}
        keyboard={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url={tileUrl || "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"}
          maxZoom={19}
        />

        {stores.map((store) => (
          <Marker
            key={store.store_id}
            position={[store.geo.lat, store.geo.long]}
            icon={walmartIcon}
          >
            <Popup className="custom-popup" maxWidth={300}>
              <div className="p-2">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 bg-primary-100 p-2 rounded-lg">
                    <Store className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Walmart Supercenter
                      </h3>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                        #{store.store_id}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-start space-x-2">
                        <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-gray-700">
                          <div className="font-medium">{store.address}</div>
                          <div className="text-gray-500">
                            {store.postal_code}
                          </div>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-gray-200">
                        <div className="text-xs text-gray-500">
                          Coordinates: {store.geo.lat.toFixed(6)},{" "}
                          {store.geo.long.toFixed(6)}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex space-x-2">
                      <a
                        href={`https://www.walmart.com/store/${store.store_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Store Details
                      </a>
                      <a
                        href={`https://maps.google.com/maps?q=${store.geo.lat},${store.geo.long}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 border border-primary-200 text-xs font-medium rounded-md text-primary-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Directions
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {stores.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-dark-800 rounded-lg">
          <div className="text-center">
            <MapPin className="h-8 w-8 text-gray-400 dark:text-dark-500 mx-auto mb-2" />
            <p className="text-sm text-gray-600 dark:text-dark-300">
              No store locations available
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
